import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";

const SignalRContext = createContext(null);

export const SignalRProvider = ({ children }) => {
    const socket = useRef(null);
    const isConnectedRef = useRef(false);
    const [isConnected, setIsConnected] = useState(false);
    const [message, setMessage] = useState(null);    
    
    const host = process.env.REACT_APP_SIGNALR_URI.replace(/\/$/, "");
    const hub = "AutotaskHub";
    const HEALTH_CHECK_URL = `${host}/healthcheck`;
    const HEALTH_CHECK_INTERVAL_SECS = 5;    
    
    // log config    
    const enableConnectionLogs = true, enableEventLogs = true;

    // Check server health in a configured interval
    const pingServer = () => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = `${HEALTH_CHECK_URL}?_=${Date.now()}`;
        });
    };

    useEffect(() => {
        if (process.env.REACT_APP_ENABLE_SIGNALR === "false") {
            if (socket.current) {
                socket.current.stop();
                socket.current = null;
            }
            isConnectedRef.current = false;
            setIsConnected(false);
            return;
        }

        let healthCheckInterval;

        const connect = async () => {
            if (socket.current) return;
            
            socket.current = new signalR.HubConnectionBuilder()
                .withUrl(`${host}/${hub}`)
                .build();

            socket.current.onreconnecting(() => {
                enableConnectionLogs && console.log("🔄 SignalR reconnecting...");
                isConnectedRef.current = false;
                setIsConnected(false);
            });

            socket.current.onreconnected(() => {
                enableConnectionLogs && console.log("✅ SignalR reconnected!");
                isConnectedRef.current = true;
                setIsConnected(true);
            });

            socket.current.onclose(() => {
                enableConnectionLogs && console.log("❌ SignalR connection closed");
                isConnectedRef.current = false;
                setIsConnected(false);
                socket.current = null;
            });

            socket.current.on("Message", (data) => {
                enableEventLogs && console.log(`✅ Message received: ${JSON.stringify(data)}`);
                setMessage(data)
            });

            try {
                await socket.current.start();                
                enableConnectionLogs && console.log("✅ SignalR connected!");
                enableConnectionLogs && console.log(`🌍 SignalR host: ${host}`);                
                isConnectedRef.current = true;
                setIsConnected(true);
            } catch (err) {
                enableConnectionLogs && console.error("❌ SignalR connection failed:", err);
            }
        };

        const checkHealthAndConnect = async () => {
            const isAlive = await pingServer();
            if (isAlive) {
                if (!isConnectedRef.current) {
                    await connect();
                }
            } else {
                enableConnectionLogs && console.log(`❌ [${new Date().toLocaleString()}] SignalR Server ${host} not reachable, retrying in ${HEALTH_CHECK_INTERVAL_SECS} seconds...`);
                if (socket.current) {
                    await socket.current.stop().catch(() => {});
                    socket.current = null;
                }
                isConnectedRef.current = false;
                setIsConnected(false);
            }
        };

        checkHealthAndConnect();

        healthCheckInterval = setInterval(checkHealthAndConnect, HEALTH_CHECK_INTERVAL_SECS * 1000);

        return () => {
            clearInterval(healthCheckInterval);
            if (socket.current) {
                socket.current.stop();
                socket.current = null;
            }            
        };
    }, []);
    
    return (
        <SignalRContext.Provider
            value={{                
                isConnected,
                message
            }}
        >
            {children}
        </SignalRContext.Provider>
    );
};
export const useSignalR = () => useContext(SignalRContext);